import { useCallback, useEffect, useRef, useState } from "react";
import { getWsBaseUrl } from "@/lib/api-client";

// Bug #17 (audit 2026-05-06): the prior ``useRef(`react_${Date.now()}_...`)``
// pattern computed Date.now()/Math.random() on every render even though
// ``useRef`` only consumes the initial value on first mount. The lazy
// ``ensureClientId`` pattern below evaluates the expression at most once
// per hook instance (gated on the ref being null).
//
// ``__generateClientId`` is exported solely so tests can verify the
// generated shape; it is NOT memoized at module scope so per-hook-instance
// uniqueness is preserved.
export function __generateClientId(): string {
  return `react_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

type SocketState =
  | "disconnected"
  | "connecting"
  | "connected"
  | "reconnecting"
  // Terminal: server rejected the socket with close code 1008 (auth).
  // Retrying cannot succeed without a new login, so we stop the retry loop
  // and surface this distinctly instead of a neutral "disconnected".
  | "unauthorized";

interface UseMarketSocketOptions {
  assetIds: string[];
  enabled?: boolean;
  maxReconnectAttempts?: number;
  baseReconnectDelayMs?: number;
  maxReconnectDelayMs?: number;
  reconnectJitterRatio?: number;
  throttleMs?: number;
}

interface PriceMessage {
  asset_id?: string;
  price?: number;
  timestamp?: string | number;
  type?: string;
  [key: string]: unknown;
}

export function useMarketSocket(options: UseMarketSocketOptions) {
  const {
    assetIds,
    enabled = true,
    maxReconnectAttempts = 8,
    baseReconnectDelayMs = 1000,
    maxReconnectDelayMs = 30000,
    reconnectJitterRatio = 0.2,
    throttleMs = 100, // 10fps by default to prevent React render thrashing
  } = options;

  const [state, setState] = useState<SocketState>("disconnected");
  const [lastMessage, setLastMessage] = useState<PriceMessage | null>(null);
  const [messages, setMessages] = useState<PriceMessage[]>([]);
  const [reconnectExhausted, setReconnectExhausted] = useState(false);
  const [closeReason, setCloseReason] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  // Manual reconnect()'s 50ms defer — tracked separately from the backoff
  // timer so disconnect/unmount can cancel it (an untracked timer could open
  // a websocket AFTER the hook is gone — Gemini/Qodo, PR #1896).
  const manualReconnectTimeoutRef = useRef<number | null>(null);
  const reconnectAttemptRef = useRef(0);
  // Lazy init: avoid Date.now()/Math.random() on every render. ``useRef``
  // only consumes its initial argument on first mount but Re-evaluates the
  // expression each call — gating on the ref being null keeps the work
  // strictly one-time per hook instance.
  const clientIdRef = useRef<string | null>(null);
  if (clientIdRef.current === null) {
    clientIdRef.current = __generateClientId();
  }

  // Active refs for callbacks
  const enabledRef = useRef(enabled);
  const assetIdsRef = useRef(assetIds);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    assetIdsRef.current = assetIds;
  }, [assetIds]);

  // Throttling buffers
  const messageBufferRef = useRef<PriceMessage[]>([]);
  const throttleTimeoutRef = useRef<number | null>(null);

  const flushMessages = useCallback(() => {
    if (messageBufferRef.current.length > 0) {
      const newMessages = [...messageBufferRef.current];
      messageBufferRef.current = [];
      
      setLastMessage(newMessages[0]); // The most recent message is at index 0 because we unshift
      setMessages((prev) => [...newMessages, ...prev].slice(0, 30));
    }
    throttleTimeoutRef.current = null;
  }, []);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimeoutRef.current !== null) {
      window.clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (manualReconnectTimeoutRef.current !== null) {
      window.clearTimeout(manualReconnectTimeoutRef.current);
      manualReconnectTimeoutRef.current = null;
    }
  }, []);

  const disconnect = useCallback(() => {
    clearReconnectTimer();
    if (throttleTimeoutRef.current !== null) {
      window.clearTimeout(throttleTimeoutRef.current);
      throttleTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000, "manual_disconnect");
      wsRef.current = null;
    }
    reconnectAttemptRef.current = 0;
    setReconnectExhausted(false);
    setCloseReason(null);
    setState("disconnected");
  }, [clearReconnectTimer]);

  const connect = useCallback(() => {
    if (!enabledRef.current || wsRef.current) {
      return;
    }

    const url = `${getWsBaseUrl()}/ws/market/${clientIdRef.current}`;
    setState(reconnectAttemptRef.current > 0 ? "reconnecting" : "connecting");

    try {
      wsRef.current = new WebSocket(url);
    } catch {
      wsRef.current = null;
      setState("disconnected");
      return;
    }

    wsRef.current.onopen = () => {
      reconnectAttemptRef.current = 0;
      setReconnectExhausted(false);
      setCloseReason(null);
      setState("connected");
      
      // On reconnect, immediately subscribe to current assets
      if (assetIdsRef.current.length > 0) {
        wsRef.current?.send(JSON.stringify({
          action: "subscribe",
          asset_ids: assetIdsRef.current,
        }));
      }
    };

    wsRef.current.onmessage = (event) => {
      let parsed: PriceMessage;
      try {
        parsed = JSON.parse(event.data) as PriceMessage;
      } catch {
        // Ignore malformed messages from upstream while preserving connection
        return;
      }

      // Add to buffer (newest first)
      messageBufferRef.current.unshift(parsed);

      // Schedule flush if not already scheduled
      if (throttleTimeoutRef.current === null) {
        throttleTimeoutRef.current = window.setTimeout(flushMessages, throttleMs);
      }
    };

    const thisSocket = wsRef.current;
    wsRef.current.onclose = (event) => {
      // Only process if this close event is for the CURRENT socket.
      // A new socket may have been created while this one was closing
      // (e.g. via reconnect()), and we must not clobber it or trigger a
      // spurious reconnect.
      if (wsRef.current !== thisSocket) {
        return;
      }

      wsRef.current = null;

      // 1008 (policy violation) is how the backend rejects unauthenticated
      // sockets — deterministic until the user logs in again, so retrying
      // only burns the backoff budget against a guaranteed rejection.
      if (event.code === 1008) {
        setCloseReason(event.reason || "Unauthorized");
        setReconnectExhausted(true);
        setState("unauthorized");
        return;
      }

      setState("disconnected");

      if (!enabledRef.current) {
        return;
      }

      reconnectAttemptRef.current += 1;
      if (maxReconnectAttempts > 0 && reconnectAttemptRef.current > maxReconnectAttempts) {
        setReconnectExhausted(true);
        return;
      }

      const exponentialDelay = Math.min(
        baseReconnectDelayMs * Math.pow(1.6, reconnectAttemptRef.current - 1),
        maxReconnectDelayMs,
      );
      const jitter = exponentialDelay * reconnectJitterRatio;
      const delay = Math.max(
        250,
        Math.round(exponentialDelay + (Math.random() * 2 - 1) * jitter),
      );

      setState("reconnecting");
      clearReconnectTimer();
      reconnectTimeoutRef.current = window.setTimeout(() => {
        connect();
      }, delay);
    };

    wsRef.current.onerror = () => {
      // Don't change state here, let onclose handle it for consistency
      wsRef.current?.close();
    };
  }, [
    baseReconnectDelayMs,
    clearReconnectTimer,
    maxReconnectAttempts,
    maxReconnectDelayMs,
    reconnectJitterRatio,
    throttleMs,
    flushMessages,
  ]);

  // Connection lifecycle: only responds to `enabled` changes
  useEffect(() => {
    if (enabled) {
      connect();
    } else {
      disconnect();
    }
    
    return () => {
      disconnect();
    };
  }, [enabled, connect, disconnect]);

  // Subscription lifecycle: responds to `assetIds` changes without dropping connection
  const previousAssetIdsRef = useRef<string[]>([]);
  
  useEffect(() => {
    const currentAssetIds = assetIds;
    const prevAssetIds = previousAssetIdsRef.current;
    
    // Only send subscription updates if we are connected
    if (state === "connected" && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      // 1. Unsubscribe from assets we no longer need
      const toUnsubscribe = prevAssetIds.filter(id => !currentAssetIds.includes(id));
      if (toUnsubscribe.length > 0) {
        wsRef.current.send(JSON.stringify({
          action: "unsubscribe",
          asset_ids: toUnsubscribe
        }));
      }
      
      // 2. Subscribe to new assets
      const toSubscribe = currentAssetIds.filter(id => !prevAssetIds.includes(id));
      if (toSubscribe.length > 0) {
        wsRef.current.send(JSON.stringify({
          action: "subscribe",
          asset_ids: toSubscribe
        }));
      }
    }
    
    // Update ref for next render
    previousAssetIdsRef.current = currentAssetIds;
    
  }, [assetIds, state]); // Dependency on state to ensure we resubscribe after reconnects

  return {
    state,
    lastMessage,
    messages,
    reconnectExhausted,
    closeReason,
    reconnect: () => {
      disconnect();
      // Mirror use-websocket.ts: give the closing socket a beat before the
      // replacement connects, so the old onclose fires against a null ref.
      // Tracked so disconnect/unmount can cancel it (disconnect() above
      // cleared any previously scheduled one via clearReconnectTimer).
      manualReconnectTimeoutRef.current = window.setTimeout(() => {
        manualReconnectTimeoutRef.current = null;
        connect();
      }, 50);
    },
  };
}
